import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Printer, Package } from "lucide-react";
import { format } from "date-fns";

const initialFormState = {
  document_number: '',
  document_date: format(new Date(), 'yyyy-MM-dd'),
  type: 'main_print',
  book_id: '',
  isbn: '',
  parent_print_run_id: '',
  quantity: 1000,
  cost_per_unit: 0,
  warehouse_id: '',
  expected_delivery_date: '',
  notes: '',
  edition_name: ''
};

export default function PrintRunForm({ isOpen, onClose, onSubmit, books, warehouses, printRuns }) {
  const { t, ready } = useTranslation();
  const [formData, setFormData] = useState(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasLoggedDebugRef = useRef(false);
  
  // Dev-only, log once when i18n is ready to avoid noisy duplicates under StrictMode
  useEffect(() => {
    if (!import.meta.env?.DEV) return;
    if (!ready) return;
    if (hasLoggedDebugRef.current) return;
    hasLoggedDebugRef.current = true;
    console.log('PrintRunForm render, i18n ready:', ready);
    console.log('Translation test:', t('print_run_form.title'));
    console.log('Available namespaces:', i18n.reportNamespaces.getUsedNamespaces());
    console.log('Current language:', i18n.language);
    console.log('Available languages:', i18n.languages);
    console.log('i18n store:', i18n.store.data);
    console.log('i18n options:', i18n.options);
  }, [ready, t]);

  useEffect(() => {
    if (isOpen) {
      // Генерируем номер тиража
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const hours = String(today.getHours()).padStart(2, '0');
      const minutes = String(today.getMinutes()).padStart(2, '0');
      
      setFormData(prev => ({
        ...initialFormState,
        document_number: `T-${year}${month}${day}-${hours}${minutes}`
      }));
    } else {
      setFormData(initialFormState);
    }
  }, [isOpen]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Если меняется тип на допечатка, очищаем ISBN
    if (field === 'type' && value === 'reprint') {
      setFormData(prev => ({ ...prev, isbn: '' }));
    }
    // Если меняется книга, сбрасываем родительский тираж
    if (field === 'book_id') {
      setFormData(prev => ({ ...prev, parent_print_run_id: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Валидация формы
    if (!formData.book_id) {
      alert('Пожалуйста, выберите книгу');
      return;
    }
    
    if (!formData.warehouse_id) {
      alert('Пожалуйста, выберите склад назначения');
      return;
    }
    
    if (!formData.document_number || !formData.document_date) {
      alert('Пожалуйста, заполните номер и дату документа');
      return;
    }
    
    if (!formData.quantity || formData.quantity <= 0) {
      alert('Пожалуйста, укажите корректное количество');
      return;
    }
    
    setIsSubmitting(true);

    try {
      await onSubmit(formData);
      setFormData(initialFormState);
    } catch (error) {
      console.error('Error creating print run:', error);
    }
    
    setIsSubmitting(false);
  };

  const availableParentRuns = formData.book_id
    ? printRuns.filter(pr => pr.book_id === formData.book_id && pr.type === 'main_print' && pr.isbn)
    : [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Printer className="w-5 h-5" />
            {t('print_run_form.title')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card className="border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="w-5 h-5 text-slate-600" />
                {t('print_run_form.basic_info')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="document_number">{t('print_run_form.document_number')}</Label>
                  <Input
                    id="document_number"
                    value={formData.document_number}
                    onChange={(e) => handleFieldChange('document_number', e.target.value)}
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="document_date">{t('print_run_form.document_date')}</Label>
                  <Input
                    id="document_date"
                    type="date"
                    value={formData.document_date}
                    onChange={(e) => handleFieldChange('document_date', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="type">{t('print_run_form.type')}</Label>
                  <Select value={formData.type} onValueChange={(value) => handleFieldChange('type', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="main_print">{t('print_run_form.main_print')}</SelectItem>
                      <SelectItem value="reprint">{t('print_run_form.reprint')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="book">{t('print_run_form.book')}</Label>
                  <Select value={formData.book_id} onValueChange={(value) => handleFieldChange('book_id', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('print_run_form.select_book')} />
                    </SelectTrigger>
                    <SelectContent>
                      {books.map((book) => (
                        <SelectItem key={book.id} value={book.id}>
                          {book.title} - {book.author}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {formData.type === 'reprint' && (
                <div className="space-y-2">
                  <Label htmlFor="parent_print_run_id">{t('print_run_form.parent_print_run')}</Label>
                  <Select 
                    value={formData.parent_print_run_id} 
                    onValueChange={(value) => handleFieldChange('parent_print_run_id', value)}
                    disabled={!formData.book_id}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('print_run_form.select_parent_print_run')} />
                    </SelectTrigger>
                    <SelectContent>
                      {availableParentRuns.length > 0 ? (
                        availableParentRuns.map((pr) => (
                          <SelectItem key={pr.id} value={pr.id}>
                            {t('print_run_form.print_run_with_isbn', { number: pr.document_number, isbn: pr.isbn })}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="p-4 text-center text-sm text-slate-500">{t('print_run_form.no_main_print_runs')}</div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {formData.type === 'main_print' && (
                  <div>
                    <Label htmlFor="isbn">{t('print_run_form.isbn')}</Label>
                    <Input
                      id="isbn"
                      value={formData.isbn}
                      onChange={(e) => handleFieldChange('isbn', e.target.value)}
                      placeholder="978-5-XXXXX-XXX-X"
                    />
                  </div>
                )}
                
                <div>
                  <Label htmlFor="edition_name">{t('print_run_form.edition_name')}</Label>
                  <Input
                    id="edition_name"
                    value={formData.edition_name}
                    onChange={(e) => handleFieldChange('edition_name', e.target.value)}
                    placeholder={t('print_run_form.edition_name_placeholder')}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">{t('print_run_form.production_and_warehouse')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="quantity">{t('print_run_form.quantity')}</Label>
                  <Input
                    id="quantity"
                    type="number"
                    min="1"
                    value={formData.quantity}
                    onChange={(e) => handleFieldChange('quantity', parseInt(e.target.value) || 0)}
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="cost_per_unit">{t('print_run_form.cost_per_unit')}</Label>
                  <Input
                    id="cost_per_unit"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.cost_per_unit}
                    onChange={(e) => handleFieldChange('cost_per_unit', parseFloat(e.target.value) || 0)}
                  />
                </div>
                
                <div>
                  <Label htmlFor="warehouse">{t('print_run_form.warehouse')}</Label>
                  <Select value={formData.warehouse_id} onValueChange={(value) => handleFieldChange('warehouse_id', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('print_run_form.select_warehouse')} />
                    </SelectTrigger>
                    <SelectContent>
                      {warehouses.filter(w => w.is_active).map((warehouse) => (
                        <SelectItem key={warehouse.id} value={warehouse.id}>
                          {warehouse.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="expected_delivery_date">{t('print_run_form.expected_delivery_date')}</Label>
                <Input
                  id="expected_delivery_date"
                  type="date"
                  value={formData.expected_delivery_date}
                  onChange={(e) => handleFieldChange('expected_delivery_date', e.target.value)}
                />
              </div>
              
              <div>
                <Label htmlFor="notes">{t('print_run_form.notes')}</Label>
                <Textarea
                  id="notes"
                  placeholder={t('print_run_form.notes_placeholder')}
                  value={formData.notes}
                  onChange={(e) => handleFieldChange('notes', e.target.value)}
                  className="h-24"
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-between items-center pt-6 border-t">
            <div className="text-sm text-slate-600">
              <p>{t('print_run_form.total_cost')} <span className="font-bold">₽{(formData.quantity * formData.cost_per_unit).toLocaleString()}</span></p>
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={onClose}>
                {t('print_run_form.cancel')}
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting || !formData.book_id || !formData.warehouse_id}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {isSubmitting ? t('print_run_form.creating') : t('print_run_form.create')}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}